/**
 * SendToClientModal — the "Send to client" action for an account whose RFC is
 * still in DRAFT. Surfaces the RFC access link + password and a ready-to-send
 * message (pre-filled with the account's primary contact name) that the user
 * can copy and send to the client.
 *
 * The existing RFC password can't be revealed (it's stored hashed), so the
 * modal generates a fresh one on demand via account.regeneratePassword.
 */

import React from 'react';
import {Check, Copy, ExternalLink, RefreshCcw} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Textarea} from '@common-web/components/ui/textarea';

interface SendToClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessUrl: string;
  companyName: string;
  contactName: string | null;
  /** Plaintext password from the latest generate (null until generated). */
  password: string | null;
  onGeneratePassword: () => void;
  isGeneratingPassword: boolean;
}

function buildMessage(params: {
  contactName: string | null;
  companyName: string;
  accessUrl: string;
  password: string | null;
}): string {
  const greeting = params.contactName ? `Hi ${params.contactName},` : 'Hello,';
  const passwordLine = params.password
    ? `Password: ${params.password}`
    : 'Password: (generate a password above to include it here)';
  return [
    greeting,
    '',
    `To get started, please complete the Request for Coverage (RFC) for ${params.companyName} using the secure link below:`,
    '',
    `Link: ${params.accessUrl}`,
    passwordLine,
    '',
    'The form saves as you go, so you can finish it in one sitting or come back to it later. Just reply to this message if you have any questions.',
    '',
    'Thank you!',
  ].join('\n');
}

function useCopied(): [boolean, (text: string) => void] {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);
  return [copied, copy];
}

export function SendToClientModal({
  open,
  onOpenChange,
  accessUrl,
  companyName,
  contactName,
  password,
  onGeneratePassword,
  isGeneratingPassword,
}: SendToClientModalProps): React.ReactElement {
  const defaultMessage = React.useMemo(
    () => buildMessage({contactName, companyName, accessUrl, password}),
    [contactName, companyName, accessUrl, password],
  );
  const [message, setMessage] = React.useState(defaultMessage);
  // Re-seed the editable message when the modal (re)opens or when the generated
  // template changes (e.g. a password was just generated). Manual edits persist
  // while those inputs are unchanged.
  React.useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage, open]);

  const [linkCopied, copyLink] = useCopied();
  const [pwCopied, copyPassword] = useCopied();
  const [msgCopied, copyMessage] = useCopied();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Send RFC to client</DialogTitle>
          <DialogDescription>
            Share the access link and password with the client so they can complete the Request for
            Coverage.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0 down the chain — DialogContent is a grid and these rows hold
            long unbreakable strings (URL, password); without it they refuse to
            shrink and push past the dialog edge. */}
        <div className='min-w-0 space-y-4'>
          <div>
            <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Access link
            </p>
            <div className='flex items-center gap-1.5'>
              <div
                className='min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm'
                title={accessUrl}
              >
                {accessUrl}
              </div>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='shrink-0'
                onClick={() => copyLink(accessUrl)}
                title='Copy link'
              >
                {linkCopied ? <Check className='h-4 w-4 text-emerald-600' /> : <Copy className='h-4 w-4' />}
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='shrink-0'
                onClick={() => window.open(accessUrl, '_blank')}
                title='Open wizard'
              >
                <ExternalLink className='h-4 w-4' />
              </Button>
            </div>
          </div>

          <div>
            <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Password
            </p>
            <div className='flex items-center gap-1.5'>
              <div className='min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm'>
                {password ?? 'Not generated yet'}
              </div>
              {password ? (
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='shrink-0'
                  onClick={() => copyPassword(password)}
                  title='Copy password'
                >
                  {pwCopied ? <Check className='h-4 w-4 text-emerald-600' /> : <Copy className='h-4 w-4' />}
                </Button>
              ) : null}
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='shrink-0'
                onClick={onGeneratePassword}
                disabled={isGeneratingPassword}
              >
                <RefreshCcw className='mr-1.5 h-3.5 w-3.5' />
                {isGeneratingPassword ? 'Generating…' : password ? 'Regenerate' : 'Generate'}
              </Button>
            </div>
            {!password ? (
              <p className='mt-1 text-xs text-muted-foreground'>
                The existing password can&apos;t be revealed — generate a new one to share.
              </p>
            ) : null}
          </div>

          <div>
            <div className='mb-1 flex items-center justify-between'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Message
              </p>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-auto px-2 py-1 text-xs'
                onClick={() => copyMessage(message)}
              >
                {msgCopied ? (
                  <Check className='mr-1 h-3.5 w-3.5 text-emerald-600' />
                ) : (
                  <Copy className='mr-1 h-3.5 w-3.5' />
                )}
                Copy message
              </Button>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={9}
              className='resize-none text-sm'
            />
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
